import { CallbackProcessingErrorName } from './CallbackProcessingError';
import {
  MAX_MEMORY_EVENTS,
} from './defaults';
import IndexedDbConnector from './IndexedDbConnector';
import MemoryDb from './MemoryDb';
import {
  AddOptions,
  ItemWrapperType,
  ProcessFnType,
  Resilience,
  ResilienceOptions,
} from './types';

export default class DbDelegator<T> implements Resilience<T> {

  private resilience: Resilience<T>;
  private product: string;

  constructor(product: string, options: ResilienceOptions = {}) {
    if (!product || product.length === 0 || typeof product!== 'string') {
      throw new Error('Invalid product provided');
    }
    this.product = product;

    if (options.useMemory) {
      this.resilience = new MemoryDb(product, options);
    } else {
      try {
        this.resilience = new IndexedDbConnector(product, options);
      } catch (error) {
        this.resilience = new MemoryDb(product, options);
      }
    }
  }

  addItem(item: T, options?: AddOptions): Promise<string> {
    return this.runOrFailOver(
      () => this.resilience.addItem(item, options)
    );
  }

  getItems(count?: number): Promise<ItemWrapperType<T>[]> {
    return this.runOrFailOver(
      () => this.resilience.getItems(count)
    );
  }

  deleteItems(itemIds: string[]): Promise<void> {
    return this.runOrFailOver(
      () => this.resilience.deleteItems(itemIds)
    );
  }

  getItemCount(): Promise<number> {
    return this.runOrFailOver(
      () => this.resilience.getItemCount()
    );
  }

  processItems(processFn: ProcessFnType<T>, count?: number): Promise<void> {
    return this.runOrFailOver(
      () => this.resilience.processItems(processFn, count)
    );
  }

  storeType() {
    return this.resilience.storeType();
  }

  private async runOrFailOver<R>(runnable: () => Promise<R>): Promise<R> {
    try {
      return await runnable();
    } catch (error) {
      if (error?.name === CallbackProcessingErrorName) {
        throw error;
      } else {
        await this.failOver();
        return runnable();
      }
    }
  }

  private async failOver() {
    // Attempt to get any events we can and put them into memory
    const memoryDb = new MemoryDb<T>(this.product);
    try {
      await this.resilience.processItems(
        items => memoryDb.massAddItems(items), MAX_MEMORY_EVENTS
      );
    } catch (error) {
      // Dont need to catch. We are just getting events if we can
    }
    this.resilience = memoryDb;
  }
}
